import { makeFetchClient } from 'so-fetch-js';
import { store } from '@/store/index';
import { get, hasOwnProperty, isEmpty } from '@/lib/helpers';
import { getLocalKey } from '@/store/local_store';
import {
  ACCESS_DETAILS,
  LINK_ID,
  CURRENT_ACTION_ID,
  MAGIC_LINK_ACCESS_DETAILS,
} from '@/store/store_keys';
import { setCurrentActionId } from '@/store/actions/magic_link';
import { uuid } from '@/lib/utils';
import { isCustomerAuthenticatedSync } from '@/lib/login_utils';
import { authState } from '@/store/getters/auth';
import { magicLinkState } from '@/store/getters/magic_link';
import router from '@/router';
import { getGeolocation } from '@/store/getters/app_config';
import {
  authFailureResponseInterceptor,
  handleAuthFailureError,
} from '@/lib/auth_failure';

const addAuthHeader = (config: any) => {
  const isAuthenticated = isCustomerAuthenticatedSync(authState());
  if (isAuthenticated) {
    const { access_token: accessToken } = authState();
    config.headers = {
      Accept: 'application/json',
      'Cache-Control': 'no-cache, no-store',
      Pragma: 'no-cache',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    };
    return config;
  }
  const accessDetails: any = getLocalKey(ACCESS_DETAILS);
  if (
    accessDetails &&
    hasOwnProperty(accessDetails, 'access_token') &&
    accessDetails.access_token
  ) {
    config.headers = {
      Accept: 'application/json',
      'Cache-Control': 'no-cache, no-store',
      Pragma: 'no-cache',
      'Content-Type': 'application/json',
      // Reads snake_case access_token, the actual field persisted in ACCESS_DETAILS —
      // camelCase accessToken doesn't exist on the stored object and previously sent
      // "Bearer undefined". Same fix applied in the React app the same day.
      Authorization: `Bearer ${accessDetails.access_token}`,
    };
    return config;
  }
  return config;
};

const setJsonHeaders = (config: any) => {
  config.headers = {
    ...config.headers,
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'app-platform': 'ooneipiDei0Tequ0Thaepheech8ietoh',
    'mt-request-id': uuid(),
  };
  return config;
};

const setCacheHeaders = (config: any) => {
  config.headers = {
    ...config.headers,
    'Cache-Control': 'no-cache, no-store',
    Pragma: 'no-cache',
  };
  return config;
};

const setGeolocationHeader = (config: any) => {
  const geolocation = getGeolocation();
  const { latitude = NaN, longitude = NaN, accuracy = 0 } = geolocation;

  config.headers = {
    ...config.headers,
    ...(!isEmpty(geolocation) &&
      !isNaN(latitude) &&
      !isNaN(longitude) && {
        'geo-location': `${latitude}, ${longitude}, ${accuracy}`,
      }),
  };
  return config;
};

const setMagicLinkHeaders = (config: any) => {
  let { access_token: accessToken } = magicLinkState();
  const accessDetails: any = getLocalKey(MAGIC_LINK_ACCESS_DETAILS);
  if (
    accessDetails &&
    hasOwnProperty(accessDetails, 'access_token') &&
    accessDetails.access_token
  ) {
    accessToken = accessDetails.access_token;
  }
  const state: any = store.getState();
  let linkId = get(state, 'magicLink.linkId', '');
  if (!linkId) {
    linkId = getLocalKey(LINK_ID);
  }
  let currentActionId = get(state, 'magicLink.currentActionId', '');
  if (!currentActionId) {
    currentActionId = getLocalKey(CURRENT_ACTION_ID);
  }

  config.headers = {
    Authorization: `Bearer ${accessToken}`,
    MagicLinkId: linkId,
    Accept: 'application/json',
    'Cache-Control': 'no-cache',
    'Content-Type': 'application/json',
    'app-platform': 'ooneipiDei0Tequ0Thaepheech8ietoh',
    'mt-request-id': uuid(),
  };
  if (currentActionId) {
    config.headers.MagicLinkActionId = currentActionId;
  }
  return config;
};

const resetActionId = (response: any) => {
  setCurrentActionId({ payload: { currentActionId: '' } });
  return response;
};

export const checkStatusAndTakeAction = (response: any) => {
  switch (response.status) {
    case 401:
      router.push({
        name: 'genericError',
        params: {
          errorCode: '401',
          errorMessage: 'Oops! You are not logged in',
        },
      });
      break;
    case 502:
      router.push({
        name: 'genericError',
        params: {
          errorCode: '502',
          errorMessage:
            'Something went wrong! It is not you, it is us. We are looking into it',
        },
      });
      break;
    case 500:
      router.push({
        name: 'genericError',
        params: {
          errorCode: '500',
          errorMessage:
            'Seems like our web developer has dozed off. Something has gone wrong in the server',
        },
      });
      break;
    case 400:
      router.push({
        name: 'genericError',
        params: {
          errorCode: '400',
          errorMessage: 'Bad request',
        },
      });
      break;
    default:
      return response;
  }
};

// const handleInvalidToken = async (response: SoFetchResponse<any>) => {
//     const { status = 0, ok = false }: any = response;
//     /* if (!ok && [401, 502].includes(status)) {
//         await store.dispatch(clearAccessDetails());
//         deleteLocalKey(ACCESS_DETAILS);
//         router.push({ name: 'customerLogin' });
//     } */
//     return response;
// };

/**
 * Covers the auth failure a response interceptor can never see.
 *
 * so-fetch runs its response interceptors on a resolved response only, and it
 * has no catch of its own. When the backend answers 401 without a cors header
 * the browser blocks the response and the fetch rejects instead, so the raw
 * TypeError goes straight past authFailureResponseInterceptor to the caller.
 * This wraps the client's own fetch to look at those rejections too, then
 * rethrows so every existing catch block behaves exactly as before.
 *
 * Assigned onto the instance rather than the prototype: post() and put() call
 * this.fetch internally, so they pick the wrapped one up as well.
 */
const signOutOnBlockedAuthFailure = <T extends { fetch: any }>(
  client: T
): T => {
  const clientFetch = (client.fetch as any).bind(client);
  const rootUrl = (client as any).rootUrl;
  (client as any).fetch = (url: string, options: any = {}) =>
    clientFetch(url, options).catch((err: any) => {
      const fullUrl = `${typeof rootUrl === 'function' ? rootUrl() : ''}${url}`;
      handleAuthFailureError(err, fullUrl);
      throw err;
    });
  return client;
};

export const jsonApiClient = signOutOnBlockedAuthFailure(
  makeFetchClient({
    rootUrl: () => process.env.VUE_APP_API_BASE_URL,
    requestInterceptors: [
      addAuthHeader,
      setJsonHeaders,
      setCacheHeaders,
      setGeolocationHeader,
    ],
    responseInterceptors: [authFailureResponseInterceptor],
  })
);

// The unsecure client sends no token, so a 401 or 403 here is the endpoint's own
// answer (an invite code that is not allowed, say) and says nothing about the
// customer's session - deliberately left off the auth failure interceptor.
export const unsecureApiClient = makeFetchClient({
  rootUrl: () => process.env.VUE_APP_API_BASE_URL,
  requestInterceptors: [setJsonHeaders, setCacheHeaders],
});

// The magic link clients carry their own short lived link token, not the
// customer's session, so a 401 there means the link has expired. Signing the
// customer out and sending them to a login screen would be the wrong answer for
// a flow that never had a login - the magic link views handle their own expiry.
export const magicLinkApiClient = makeFetchClient({
  rootUrl: () => `${process.env.VUE_APP_API_BASE_URL}/v1/magic_link`,
  requestInterceptors: [setMagicLinkHeaders],
  responseInterceptors: [resetActionId],
});

export const magicLinkActionApiClient = makeFetchClient({
  rootUrl: () => process.env.VUE_APP_API_BASE_URL,
  requestInterceptors: [setMagicLinkHeaders],
  responseInterceptors: [resetActionId],
});

export const freoAssisApiClient = signOutOnBlockedAuthFailure(
  makeFetchClient({
    rootUrl: () => process.env.VUE_APP_FREO_ASSIST_BASE_URL,
    responseInterceptors: [authFailureResponseInterceptor],
  })
);

/**
 * For the calls that pass a full url of their own instead of a path under a
 * root - api/freoAssist.ts and api/repayments.ts both did this with so-fetch's
 * default client, which has no interceptors and so let a 401 through unnoticed.
 * They send the customer's Bearer token, so they belong behind the same guard.
 */
export const absoluteUrlApiClient = signOutOnBlockedAuthFailure(
  makeFetchClient({
    responseInterceptors: [authFailureResponseInterceptor],
  })
);
